import {
  createDisplayBounds,
  normalizeBounds,
  normalizeThreshold,
  resolveAxisDisplayUnit,
  resolveValueDisplayUnit,
} from './data'
import { getBoilerTubeNumbering } from './boilerTubeNumbering'
import {
  AxisTickMode,
  ReportAxisMode,
  ReportComponentOverlayMode,
  ReportLabelMode,
  ReportOverlayMode,
  type ReportHeatmapDescriptor,
  type ReportHeatmapRequest,
} from './types'
import { fnv1a64, stableStringify } from './utils'

export const REPORT_HEATMAP_RENDERER_VERSION = 7 as const

export function createReportHeatmapDescriptor(
  request: ReportHeatmapRequest
): ReportHeatmapDescriptor {
  const normalizedBounds = normalizeBounds(request.cdf, request.bounds)
  const displayBounds = createDisplayBounds(normalizedBounds, {
    x: request.axisMetadata?.x?.displayUnit,
    y: request.axisMetadata?.y?.displayUnit,
  })
  const threshold = normalizeThreshold(
    request.colorThreshold,
    request.cdf.data.params.measurement_unit,
    request.valueDisplayUnit ??
      resolveValueDisplayUnit(request.cdf.data.params.measurement_unit)
  )

  const descriptorWithoutHash: Omit<ReportHeatmapDescriptor, 'hash'> = {
    version: REPORT_HEATMAP_RENDERER_VERSION,
    kind: request.kind,
    componentId: request.componentId,
    sectionKey: request.sectionKey,
    cdfId: request.cdfId,
    legendTitle: request.legendTitle,
    bounds: {
      xStart: displayBounds.x.start,
      xEnd: displayBounds.x.end,
      xUnit: displayBounds.x.unit.id,
      yStart: displayBounds.y.start,
      yEnd: displayBounds.y.end,
      yUnit: displayBounds.y.unit.id,
    },
    threshold: threshold
      ? {
          min: threshold.min,
          mid: threshold.mid,
          max: threshold.max,
          unit: threshold.unit.id,
        }
      : undefined,
    outputWidthPx: request.outputWidthPx,
    axisMode: request.includeAxes
      ? ReportAxisMode.Embedded
      : ReportAxisMode.None,
    axisMetadata: request.axisMetadata
      ? {
          x: request.axisMetadata.x
            ? {
                name: request.axisMetadata.x.name,
                displayUnit: request.axisMetadata.x.displayUnit?.id,
              }
            : undefined,
          y: request.axisMetadata.y
            ? {
                name: request.axisMetadata.y.name,
                displayUnit: request.axisMetadata.y.displayUnit?.id,
              }
            : undefined,
        }
      : undefined,
    boilerTubeNumbering: getBoilerTubeNumbering(
      request.componentModel,
      request.axisMetadata?.x?.displayUnit ??
        resolveAxisDisplayUnit(normalizedBounds.rect.x.unit)
    ),
    axisTickMode: AxisTickMode.Nice,
    heatmapMode:
      request.includeHeatmap === false ? ReportOverlayMode.None : undefined,
    legendMode: 'separate' as const,
    componentOverlayMode:
      request.includeComponentOverlay === false
        ? ReportOverlayMode.None
        : (request.componentOverlayMode ?? ReportComponentOverlayMode.Standard),
    includeSectionLabels: request.includeSectionLabels !== false,
    includeWeldOverlays: request.includeWeldOverlays !== false,
    overlaySignature: request.overlaySignature,
    nominalDisplayUnit: request.nominalDisplayUnit?.id,
    overlayMode: request.includeOverlay
      ? request.overlayMode
      : ReportOverlayMode.None,
    labelMode: request.includeOverlay
      ? request.labelMode
      : ReportLabelMode.None,
    background: request.background,
  }

  return {
    ...descriptorWithoutHash,
    hash: hashReportHeatmapDescriptor(descriptorWithoutHash),
  }
}

export function hashReportHeatmapDescriptor(
  descriptor: Omit<ReportHeatmapDescriptor, 'hash'> | ReportHeatmapDescriptor
) {
  const rest = { ...descriptor } as Partial<ReportHeatmapDescriptor>
  delete rest.hash
  return fnv1a64(stableStringify(rest))
}
